import React, { useEffect, useRef } from 'react';
import { Layout, Typography, Tag, Button, Space, Flex, theme, Divider, Card } from 'antd';
import {
  ExpandOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
  RedoOutlined,
  PlusOutlined,
  LeftOutlined,
  SunOutlined
} from '@ant-design/icons';
import { ProcessFlow } from '../../../../types';
import './WorkflowDetail.css';

// Type declaration for n8n-demo custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'n8n-demo': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        workflow?: string;
        frame?: string;
        theme?: string;
      };
    }
  }
}

const { Footer } = Layout;
const { Title, Text, Link } = Typography;
const { useToken } = theme;

interface WorkflowDetailProps {
  workflow: ProcessFlow;
  onBack: () => void;
}

// Default workflow structure if no diagram is available
const defaultWorkflow = {
  nodes: [
    {
      parameters: {
        functionCode: 'return [{ json: { hello: "world" } }];'
      },
      name: 'Start',
      type: 'n8n-nodes-base.function',
      typeVersion: 1,
      position: [250, 300]
    },
    {
      parameters: {
        message: '=Hello {{$json["hello"]}}'
      },
      name: 'Message',
      type: 'n8n-nodes-base.set',
      typeVersion: 1,
      position: [450, 300]
    }
  ],
  connections: {
    Start: {
      main: [[{ node: 'Message', type: 'main', index: 0 }]]
    }
  }
};

const FlowChart: React.FC<{ workflow: ProcessFlow }> = ({ workflow }) => {
  const ref = useRef<any>(null);

  useEffect(() => {
    if (ref.current) {
      const diagram = workflow.diagram && Object.keys(workflow.diagram).length > 0
        ? workflow.diagram
        : { nodes: [], connections: {} };
      ref.current.setAttribute('workflow', JSON.stringify(diagram));
      ref.current.setAttribute('frame', 'true');
      ref.current.setAttribute('theme', 'light');
    }
  }, [workflow.diagram]);

  return (
    <div className="workflow-detail-chart">
      <n8n-demo style={{ width: '100%' }} ref={ref}></n8n-demo>
    </div>
  );
};

export const WorkflowDetail: React.FC<WorkflowDetailProps> = ({ workflow, onBack }) => {
  /**
 * Executes the workflow by opening the execution URL in a new window
 * Supports both GET and POST methods
 */
  const handleExecuteWorkflow = () => {

    const url = workflow.workflowExcectionUrl || '';
    const method = workflow.workflowExecutionMethod?.toUpperCase() || 'GET';

    if (method === 'GET') {
      // Opens URL directly in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (method === 'POST') {
      // Creates and submits a form to open in new tab
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url || '';
      form.target = '_blank';
      form.style.display = 'none';

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  };
  return (
    <Card>
      <Layout className="workflow-detail-container">
        <Layout.Content className="workflow-detail-content">
          <Flex vertical gap="var(--ant-marginXS)">
            <Flex align="center" justify="space-between" gap="var(--ant-marginXS)">
              <Flex align="center" gap="var(--ant-marginXS)">
                <Title level={2} className="workflow-detail-title">
                  {workflow.name}
                </Title>
                <Tag className={`status-tag ${workflow.status}`}>
                  {workflow.status}
                </Tag>
              </Flex>
              {/* <Button id="run-flow-button">Run flow</Button>
          <Button id="set-live-button">Set live</Button>
          <Button id="save-flow-button">Save</Button> */}
              <Button type="primary" href={workflow.n8nWebhookURL} target="_blank" id="edit-flow-button">
                Edit
              </Button>
            </Flex>
            <Text type="secondary" className="workflow-detail-description">
              {workflow.description}
            </Text>
            <Divider style={{ margin: '12px 0' }} />
            <FlowChart workflow={workflow} />
          </Flex>
        </Layout.Content>

        <Footer className="workflow-detail-footer">
          <Flex justify="center" align="center">
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                id="execute-workflow-button"
                onClick={handleExecuteWorkflow}
              >
                Execute workflow
              </Button>
            </Space>
          </Flex>
        </Footer>
      </Layout>
    </Card>
  );
};
